import { CollectionConfig } from "@rebasepro/types";
import companiesCollection from "./companies";

/**
 * Company Leads — the commercial relationship with a company, kept off the
 * company row itself.
 *
 * These three fields lived on `companies` until the RLS audit
 * (`scripts/check-rls.sh`) made the consequence concrete: `companies` carries
 * `public_read` with `USING (true)`, because the public job board has to render
 * every vacancy's company name, logo and brand colours for signed-out visitors.
 * RLS is row-level and cannot exclude columns, so that one policy also served
 * `lead_status`, `needs_description` and `requested_services` to anonymous
 * callers. It was not theoretical: a `GET /api/data/companies` with no token
 * returned all thirty columns, and a probe row came back reading
 * `lead_status: contacted` with the budget and the decision-maker in the notes.
 *
 * The fix has to be a different table rather than a narrower policy, because
 * neither RLS nor a column GRANT can express "these columns, for the members of
 * this row". Column privileges are role-wide and every request-path caller is
 * the same `rebase_user`; a view has the same problem. Splitting the table turns
 * a column question back into a row question, which RLS answers natively.
 *
 * Access, therefore, is the inverse of `companies`: nothing is public here.
 */
const companyLeadsCollection: CollectionConfig = {
    name: "Empresas que nos escribieron",
    singularName: "Empresa que nos escribió",
    slug: "company-leads",
    table: "company_leads",
    // Own this table's RLS outright. The generator otherwise injects
    // `company_leads_default_admin_*` policies keyed on `auth.uid() IS NULL OR
    // admin`, and it re-injects them on every boot — so a table created
    // specifically to stop over-serving would quietly regain a policy nobody
    // wrote, in production, after a restart. `admin_full_access` below already
    // covers the team, and the cron and admin paths reach this table through the
    // owner connection, which is not subject to these policies.
    disableDefaultPolicies: true,
    securityRules: [
        // No `public_read` equivalent, deliberately — that omission is the whole
        // point of this table. A caller with no membership sees nothing.
        {
            // The company's own brief, readable by its own members.
            //
            // The `company_members` subquery runs under the caller's own RLS
            // visibility, and `member_own_row` there shows a caller only their
            // own membership — which is exactly what makes this correct: the
            // EXISTS can only ever be satisfied by the caller's real
            // memberships. (Contrast `claim_unclaimed_company` on
            // company_members, where caller-filtered visibility silently broke a
            // NOT EXISTS. Same mechanism, opposite consequence, because this is
            // a positive check.) It is the same idiom `company_read_own_jobs`
            // and `company_read_own_applications` already use.
            name: "member_read_own_lead",
            operation: "select",
            using: "EXISTS (SELECT 1 FROM company_members cm WHERE cm.auth_user_id = auth.uid() AND cm.company_id = company_leads.company_id)"
        },
        {
            name: "member_update_own_lead",
            operation: "update",
            using: "EXISTS (SELECT 1 FROM company_members cm WHERE cm.auth_user_id = auth.uid() AND cm.company_id = company_leads.company_id)",
            withCheck: "EXISTS (SELECT 1 FROM company_members cm WHERE cm.auth_user_id = auth.uid() AND cm.company_id = company_leads.company_id)"
        },
        {
            // Registration writes this row, and it must therefore be insertable
            // by the member who has just claimed the company — which is why
            // RegisterCompanyForm creates it *after* the company_members row,
            // not before. Insert it first and this check finds no membership and
            // refuses, which is the correct answer to the wrong order.
            //
            // `using` is required by the type and ignored for INSERT (Postgres
            // has no USING clause there), so it mirrors `withCheck`.
            name: "member_insert_own_lead",
            operation: "insert",
            using: "EXISTS (SELECT 1 FROM company_members cm WHERE cm.auth_user_id = auth.uid() AND cm.company_id = company_leads.company_id)",
            withCheck: "EXISTS (SELECT 1 FROM company_members cm WHERE cm.auth_user_id = auth.uid() AND cm.company_id = company_leads.company_id)"
        },
        {
            name: "admin_full_access",
            operation: "all",
            roles: ["admin"]
        }
    ],
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid"
        },
        company_id: {
            name: "Empresa",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => companiesCollection,
                localKey: "company_id"
            },
            validation: { required: true },
            description: "La empresa a la que pertenece este registro comercial"
        },
        employer_key: {
            name: "Empresa a contactar",
            type: "string",
            description: "Se rellena cuando alguien pasa este lead a «Empresas que contratan». Vacío = nadie lo ha trabajado todavía, que es lo que cuenta la cola de entrantes. A partir de ahí el estado que manda es el de la empresa a contactar, no el de aquí.",
            admin: { readOnly: true }
        },
        // The ids the public form submits, with the same wording it shows —
        // `RegisterCompanyForm.tsx:208` and `AppRouter.tsx:1257`. Without the
        // enum the admin printed the raw id ("attraction_optimization"), so the
        // two halves of the product named the same service differently.
        requested_services: {
            name: "Servicios solicitados",
            type: "array",
            columnType: "text[]",
            of: {
                name: "Servicio",
                type: "string",
                enum: [
                    { id: "headhunting", label: "Selección y contratación de talento", color: "green" },
                    { id: "attraction_optimization", label: "Atracción y optimización de procesos", color: "blue" }
                ]
            },
            description: "Lo que pidió la empresa al registrarse"
        },
        needs_description: {
            name: "Necesidad descrita",
            type: "string",
            admin: {
                multiline: true
            },
            description: "Lo que la empresa dice necesitar, en sus palabras. Nunca es público."
        },
        lead_status: {
            name: "Estado",
            type: "string",
            enum: [
                { id: "none", label: "Sin clasificar", color: "gray" },
                { id: "new", label: "Nuevo", color: "blue" },
                { id: "contacted", label: "Contactado", color: "orange" },
                { id: "converted", label: "Ya es cliente", color: "green" }
            ],
            defaultValue: "none",
            description: "Estado comercial interno. Lo escribe el equipo, no la empresa."
        },
        created_at: {
            name: "Creado",
            type: "date",
            autoValue: "on_create"
        },
        updated_at: {
            name: "Actualizado",
            type: "date",
            autoValue: "on_update"
        }
    },
    admin: {
        group: "Captación",
        icon: "Coins",
        // Out of the menu since 2026-09-06. A company that wrote to us is
        // worked from "Empresas que contratan → Nos escribieron", which reads
        // these rows; listed beside it, this was the raw table with a column
        // that said "Estado" and showed names. Reachable by URL.
        hideFromNavigation: true,
        sort: ["created_at", "desc"],
        /**
         * A lead is a company asking for something, and the list showed
         * neither: every row was an untitled strip with a "Nuevo" chip and a
         * truncated service name, five in a row, indistinguishable.
         *
         * `previewProperties` had already been pointed at `company_id` to fix
         * exactly this and could not — it ranks properties of *this* row and the
         * derivation skips relations, so the title fell through to the first
         * enum it found. A declared display path is the thing that can say it.
         *
         * `needs_description` as the subtitle because it is the lead: what they
         * typed into the box is the whole reason to call them back.
         */
        display: {
            title: "company_id",
            // Services and the free-text brief, in one line. Neither survived as
            // a column: the list view renders a single field between the title
            // and the date, so whichever of the two came second was dropped
            // silently — and `lead_status`, the field this screen is triaged on,
            // was the one being dropped. It gets the column; these get the
            // subtitle, where they are prose anyway.
            subtitle: ({ entity }) => {
                const v = entity.values as {
                    requested_services?: string[] | null;
                    needs_description?: string | null;
                };
                const labels: Record<string, string> = {
                    headhunting: "Selección y contratación",
                    attraction_optimization: "Atracción y optimización"
                };
                const services = (v.requested_services ?? []).map(s => labels[s] ?? s);
                return [services.join(", "), v.needs_description]
                    .filter(Boolean).join(" — ") || undefined;
            },
            date: "created_at"
        },
        listProperties: ["lead_status", "created_at"],
        previewProperties: ["company_id", "lead_status"]
    }
};

export default companyLeadsCollection;
